import { EditRankingForm } from 'components/InviteTeamMembersByCSVModal/views/EditRanking/TeamMemberEditRanking.constants';
import { CUSTOMER_POSITIONS, CUSTOMER_TEAMS } from 'constants/endpoints';
import { useFormBehaviors } from 'hooks';
import {
  UseFormBehaviors,
  UseFormBehaviorsEventType,
  UseFormBehaviors_Options,
} from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { FC, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { toastService } from 'services';
import i18next from 'i18next';
import {
  API_POST_editRankingMultiLocationTieringCSV,
  API_POST_editRankingTieringCSV,
} from 'requests/POST_EditRankingCSV';
import {
  inviteTeaMemberCSVModalRoutingService,
  useInviteTeamMembersByCSVModalUIContext,
} from 'components/InviteTeamMembersByCSVModal/InviteTeamMembersByCSVModal.ui.context';
import { CsvError } from 'components/InviteTeamMembersByCSVModal/views/ImportViaSpreadsheet/TeamMemberCSVView.constants';
import { METHOD_GET } from 'requests';
import { saveResponseAsCSV } from 'utils/saveResponseAsCSV';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { TeamMemberPageContext } from 'pages/TeamMembers/Internal/TeamMemberPage.context';
import { isMultiLocationTieringFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';
import { capitalize } from 'utils/capitalize';

type TeamMemberEditRankingFormContextType = UseFormBehaviors<EditRankingForm> & {
  onDownload: () => {};
  isSubmitting: boolean;
};

type EditRankingErrorResponseData =
  | CsvError[]
  | { type?: string; error_messages?: Record<string, string[] | string> };

const formatErrorMessageColumn = (key: string): string => {
  const attribute = key.split('.').pop() || key;
  if (attribute === 'base') return '-';
  return capitalize(attribute.replace(/_/g, ' '));
};

const mapErrorResponseToCsvErrors = (data?: EditRankingErrorResponseData): CsvError[] => {
  if (Array.isArray(data)) return data;
  if (data?.error_messages && typeof data.error_messages === 'object') {
    return Object.entries(data.error_messages).flatMap(([key, messages]) =>
      (Array.isArray(messages) ? messages : [messages]).map((message) => ({
        row: '-' as const,
        column: formatErrorMessageColumn(key),
        error: message,
      })),
    );
  }
  return [];
};

const VALIDATION_position_id = (value: string[]) => {
  if (value.length === 0 || !value[0]) {
    return i18next.t('team_member:POSITION_IS_REQUIRED');
  }
  return '';
};

const VALIDATION_CSV = (file: File | undefined) => {
  if (!file) {
    return i18next.t('team_member:FILE_IS_REQUIRED');
  }
  const allowedFileTypes = ['csv'];
  const fileNameParts = file.name.split('.');
  const fileExtension = fileNameParts[fileNameParts.length - 1].toLowerCase();
  if (!allowedFileTypes.includes(fileExtension)) {
    return i18next.t('team_member:FILE_NOT_CORRECT_FORMAT_UPLOAD_CSV_FILE_AND_TRY_AGAIN');
  }
  return '';
};

const TeamMemberEditRankingInitialState = {
  csv: undefined,
  location_ids: [],
  position_id: [],
};

export const TeamMemberEditRankingFormContext = createContext<TeamMemberEditRankingFormContextType>(
  {} as TeamMemberEditRankingFormContextType,
);

export const TeamMemberEditRankingFormProvider: FC = ({ children }) => {
  const { apiRequest } = useAPIActions();
  const { goToViewError } = useInviteTeamMembersByCSVModalUIContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    refetch,
    values: { location },
  } = useContext(TeamMemberPageContext);

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(location?.value)),
  );

  const selectedCommunity = Number(location.id);

  const handleSubmitError = (error?: AxiosResponse) => {
    setIsSubmitting(false);
    const csvErrors = mapErrorResponseToCsvErrors(error?.data);
    if (!csvErrors.length) {
      csvErrors.push({ row: '-', column: '-', error: i18next.t('errors:SOMETHING_WENT_WRONG') });
    }
    goToViewError({ csvErrors });
  };

  const handleTieringSubmit = async (values: EditRankingForm) => {
    const selectedPosition = values?.position_id[0];
    const formData = new FormData();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { csv } = values as any;
    formData.append('csv_file', csv);
    setIsSubmitting(true);
    apiRequest(API_POST_editRankingTieringCSV({ formData, selectedPosition, selectedCommunity }), {
      onSuccess: () => {
        setIsSubmitting(false);
        if (refetch) refetch();
        toastService.success(i18next.t('team_member:RANKING_SUCCESSFULLY_IMPORTED'));
        inviteTeaMemberCSVModalRoutingService.close();
      },
      onError: handleSubmitError,
    });
  };

  const handleMultiLocationTieringSubmit = async ({ csv }: EditRankingForm) => {
    const formData = new FormData();
    formData.append('csv[upload]', csv as Blob);
    if (csv) {
      setIsSubmitting(true);
      apiRequest(API_POST_editRankingMultiLocationTieringCSV({ formData }), {
        onSuccess: () => {
          setIsSubmitting(false);
          if (refetch) refetch();
          toastService.success(i18next.t('team_member:RANKING_SUCCESSFULLY_IMPORTED'));
          inviteTeaMemberCSVModalRoutingService.close();
        },
        onError: handleSubmitError,
      });
    }
  };

  const addEditRankingRuleFormBehaviorsOptions: UseFormBehaviors_Options<EditRankingForm> = {
    initialState: { ...TeamMemberEditRankingInitialState },
    isDirtyCheckEnabled: true,
    type: 'CREATE',
    onSubmit: isMultiLocationTieringEnabled
      ? handleMultiLocationTieringSubmit
      : handleTieringSubmit,
    validations: {
      csv: VALIDATION_CSV,
      ...(!isMultiLocationTieringEnabled && { position_id: VALIDATION_position_id }),
    },
  };

  const addEditRankingRuleForm = useFormBehaviors<EditRankingForm>(
    addEditRankingRuleFormBehaviorsOptions,
  );
  const onMultiLocationTieringDownload = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_GET,
          responseType: 'blob',
          url: `${CUSTOMER_TEAMS()}/csv/export`,
          query: {
            csv: { filter: { with_primary_community: addEditRankingRuleForm.values.location_ids } },
          },
        },
        {
          onSuccess: (response) => {
            saveResponseAsCSV(response, `seniority_ranking-${Date.now()}.csv`);
          },
          onError: handleGenericErrorMessage,
        },
      ),
    [addEditRankingRuleForm.values.location_ids, apiRequest],
  );

  const selectedPosition = addEditRankingRuleForm?.values?.position_id[0];
  const onTieringDownload = useCallback(
    () =>
      apiRequest(
        {
          __typename: 'LegacyAPI',
          method: METHOD_GET,
          responseType: 'blob',
          url: `${CUSTOMER_POSITIONS()}/${selectedPosition}/${selectedCommunity}/export_csv.csv`,
        },
        {
          onSuccess: (response) => {
            saveResponseAsCSV(response, `seniority_ranking-${Date.now()}.csv`);
          },
          onError: handleGenericErrorMessage,
        },
      ),
    [apiRequest, selectedCommunity, selectedPosition],
  );

  const value = useMemo(
    () => ({
      ...addEditRankingRuleForm,
      onChange: (e: UseFormBehaviorsEventType | UseFormBehaviorsEventType[]) => {
        if (Array.isArray(e)) {
          // If e is an array of events, iterate through them
          e.forEach(({ target: { value = '', name = 'csv', ...rest } }) => {
            addEditRankingRuleForm.onChange({ target: { value, name, ...rest } });
          });
        } else {
          const {
            target: { value = '', name = 'csv', ...rest },
          } = e;
          addEditRankingRuleForm.onChange({
            target: { name, value, ...rest },
          });
        }
      },
      onDownload: isMultiLocationTieringEnabled
        ? onMultiLocationTieringDownload
        : onTieringDownload,
      isDisabled: isMultiLocationTieringEnabled
        ? !addEditRankingRuleForm.values.location_ids.length
        : VALIDATION_position_id(addEditRankingRuleForm.values.position_id) !== '',
      isSubmitting,
    }),
    [
      addEditRankingRuleForm,
      isMultiLocationTieringEnabled,
      isSubmitting,
      onMultiLocationTieringDownload,
      onTieringDownload,
    ],
  );
  return (
    <TeamMemberEditRankingFormContext.Provider value={value}>
      {children}
    </TeamMemberEditRankingFormContext.Provider>
  );
};

export const useTeamMemberEditRankingFormContext = (): TeamMemberEditRankingFormContextType =>
  useContext(TeamMemberEditRankingFormContext);
